import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Link } from '@tanstack/react-router'
import {
  SortingState,
  createColumnHelper,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { startOfWeek, subWeeks } from 'date-fns'
import { useState } from 'react'
import z from 'zod'
import { DateRangePicker } from '~/components/date-picker'
import { DurationCell } from '~/components/table'
import { WithPermission, usePermission } from '~/features/access-control'
import { Avatar } from '~/features/avatar'
import {
  ChannelsCommandList,
  CommandPopover,
  GroupsCommandList,
  ScorecardsCommandList,
  UsersCommandList,
  WorkspacesCommandList,
} from '~/features/command-lists'
import { useFilterAccess } from '~/features/filter-control'
import { Table } from '~/features/table/ui/table'
import { useTimespanPreference, useWeekStartsOn } from '~/hooks'
import { QueryConfig, useAnalytics } from '~/modules/analytics/hooks/useAnalytics'
import { useLoader } from '~/modules/loader'

import { AnalyticsQueryAggregate, FilterCollection } from '@hap/analytics'
import { Button, Heading1, Icon, ToggleGroup, ToggleGroupItem, cn } from '@hap/components'
import {
  NEGATIVE_SENTIMENT_LABEL_ID,
  NEUTRAL_SENTIMENT_LABEL_ID,
  POSITIVE_SENTIMENT_LABEL_ID,
} from '@hap/constants'
import { Timespan, toPercent } from '@hap/utils'

import { useAnalyticsPresets } from '../indicators/-components/hooks'
import {
  hasEditableMetricFilters,
  preserveReadOnlyMetricFilters,
} from '../indicators/-components/metric-filter-access'
import { MetricFilters, metricFilters } from '../indicators/-components/metric-widget'
import { ExportQuery } from './-components/export-query'

const defaultSorting = [{ id: 'aqs', desc: true }] satisfies SortingState

export const searchParamsSchema = z.object({
  sort: z.object({ id: z.string(), desc: z.boolean() }).array().optional(),
  filters: metricFilters.default({}),
  timespan: Timespan.Info.optional(),
})

type SearchParams = z.infer<typeof searchParamsSchema>

export const Route = createFileRoute('/_authenticated/$alias/_app/leaderboard/')({
  component: RouteComponent,
  validateSearch: searchParamsSchema,
})

const AGGREGATIONS = {
  aqs: {
    field: 'averageEvaluationScore',
    op: 'avg',
  },
  passRate: {
    op: 'countIf',
    conditions: [
      { field: 'metQualityGoal', op: 'eq', value: true },
      // { field: 'publishedAt', op: 'isNotNull' },
    ],
  },
  totalEvaluations: {
    op: 'countIf',
    conditions: [
      { field: 'hasEvaluation', op: 'eq', value: true },
      // { field: 'publishedAt', op: 'isNotNull' },
    ],
  },
  aht: {
    op: 'avg',
    field: 'handleTime',
    dataset: 'interactions',
  },
  totalInteractions: {
    op: 'countDistinct',
    field: 'id',
    dataset: 'interactions',
  },
  eligibleInteractions: {
    op: 'countEligibleInteractions',
    field: 'id',
    dataset: 'interactions',
  },
  evaluatedEligibleInteractions: {
    op: 'countEvaluatedEligibleInteractions',
    field: 'id',
    dataset: 'interactions',
  },
  negativeSentimentCount: {
    op: 'countIf',
    conditions: [
      {
        field: 'overallCustomerSentimentLabelId',
        op: 'eq',
        value: NEGATIVE_SENTIMENT_LABEL_ID,
        dataset: 'interactions',
      },
    ],
  },
  positiveSentimentCount: {
    op: 'countIf',
    conditions: [
      {
        field: 'overallCustomerSentimentLabelId',
        op: 'eq',
        value: POSITIVE_SENTIMENT_LABEL_ID,
        dataset: 'interactions',
      },
    ],
  },
  neutralSentimentCount: {
    op: 'countIf',
    conditions: [
      {
        field: 'overallCustomerSentimentLabelId',
        op: 'eq',
        value: NEUTRAL_SENTIMENT_LABEL_ID,
        dataset: 'interactions',
      },
    ],
  },
  sentimentLift: {
    op: 'avg',
    field: 'sentimentInfluence',
  },
} satisfies Record<string, AnalyticsQueryAggregate>

function RouteComponent() {
  const canViewLeaderboard = usePermission({
    resource: 'leaderboard',
    actions: ['view_all'],
  })
  if (!canViewLeaderboard) {
    return (
      <div className="gutters my-10 flex flex-col items-center justify-center gap-4">
        <Icon name="x-circle" className="text-destructive size-10" />
        <Heading1>Access Denied</Heading1>
        <p className="text-accent-foreground">
          You do not have permission to view the leaderboard.
        </p>
      </div>
    )
  }

  const { isFilterAllowed } = useFilterAccess()
  const [modality, setModality] = useState<string>('handledBy')
  const { filters, timespan: selectedTimespan, sort } = Route.useSearch()
  const weekStartsOn = useWeekStartsOn()
  const { timespan, setTimespanPreference } = useTimespanPreference({
    selectedTimespan,
    startOffset: (date) => startOfWeek(subWeeks(date, 3), { weekStartsOn }),
  })
  const navigate = useNavigate({ from: Route.fullPath })
  const daterange = Timespan.toStaticTimespan(timespan, weekStartsOn)
  const presets = useAnalyticsPresets({
    filters,
    timespan: daterange,
    baseFilters: [],
  })

  const aggregations: Record<string, AnalyticsQueryAggregate> =
    modality === 'handledBy'
      ? {
          handledBy: {
            field: 'handledBy',
            hydrate: true,
            dataset: 'interactions',
          },
          ...AGGREGATIONS,
        }
      : modality === 'scorecardId'
        ? {
            scorecardId: {
              field: 'scorecardId',
              hydrate: true,
              dataset: 'evaluations',
            },
            ...AGGREGATIONS,
          }
        : {
            groupIds: {
              field: 'groupIds',
              hydrate: true,
              dataset: 'interactions',
            },
            ...AGGREGATIONS,
          }

  const query = {
    timespan: presets.timespan,
    dataset: 'interactions',
    filters: presets.filters,
    aggregations: aggregations,
  } satisfies QueryConfig
  const { data, loading } = useAnalytics(query)

  const table = useReactTable({
    columns,
    data: (data ?? initialData) as Array<LeaderboardRow>,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    enableSortingRemoval: false,
    onSortingChange: (sorter) => {
      const s = typeof sorter === 'function' ? sorter(sort ?? []) : sorter
      navigate({
        search: (prev) => ({
          ...prev,
          sort: s,
          p: 0,
        }),
      })
    },
    state: {
      sorting: sort || defaultSorting,
    },
    defaultColumn: {
      size: 0,
      minSize: 0,
      maxSize: Number.MAX_SAFE_INTEGER,
    },
  })

  useLoader(loading)

  return (
    <div className="gutters my-10 shrink grow space-y-10">
      <header className="space-y-3">
        <hgroup className="flex items-center gap-4">
          <Heading1>Leaderboard</Heading1>
          <ToggleGroup
            type="single"
            value={modality}
            onValueChange={(value) => {
              if (value) {
                setModality(value)
              } else {
                // Cycle through options when clicking the currently selected one
                const order = ['handledBy', 'scorecardId', 'groupIds']
                const currentIndex = order.indexOf(modality)
                setModality(order[(currentIndex + 1) % order.length])
              }
            }}
          >
            <ToggleGroupItem value="handledBy">Agent</ToggleGroupItem>
            <ToggleGroupItem value="scorecardId">Scorecards</ToggleGroupItem>
            <ToggleGroupItem value="groupId">Groups</ToggleGroupItem>
          </ToggleGroup>
        </hgroup>
        <div className="flex justify-between">
          <div className="flex flex-wrap items-center gap-2">
            {hasEditableMetricFilters(filters, isFilterAllowed) && (
              <Button
                variant="ghost"
                onClick={() =>
                  navigate({
                    search: (prev) => ({
                      ...prev,
                      filters: preserveReadOnlyMetricFilters(
                        prev.filters,
                        isFilterAllowed,
                      ),
                    }),
                  })
                }
                icon="arrow-counter-clockwise"
              >
                Reset
              </Button>
            )}

            <DateRangePicker
              value={timespan}
              onValueChange={(timespan) => {
                setTimespanPreference(timespan)
                navigate({
                  search: (prev) => ({ ...prev, timespan }),
                })
              }}
            />

            <CommandPopover
              filterField="workspaceId"
              label="Workspaces"
              collection={FilterCollection.Workspaces}
              selected={filters.workspaceIds?.values || []}
            >
              <WorkspacesCommandList
                multiselect
                onSelect={(ids) =>
                  handleFilterValueChange({
                    field: 'workspaceIds',
                    values: ids,
                  })
                }
              />
            </CommandPopover>

            <CommandPopover
              filterField="channelId"
              collection={FilterCollection.Channels}
              label="Channels"
              selected={filters.channelIds?.values || []}
            >
              <ChannelsCommandList
                multiselect
                onSelect={(ids) =>
                  handleFilterValueChange({
                    field: 'channelIds',
                    values: ids,
                  })
                }
              />
            </CommandPopover>

            <CommandPopover
              filterField="scorecardId"
              collection={FilterCollection.Scorecards}
              label="Scorecards"
              selected={filters.scorecardIds?.values || []}
            >
              <ScorecardsCommandList
                multiselect
                onSelect={(ids) =>
                  handleFilterValueChange({
                    field: 'scorecardIds',
                    values: ids,
                  })
                }
                contextFilters={{
                  workspaceId: {
                    $eq: filters.workspaceIds?.values || [],
                  },
                }}
              />
            </CommandPopover>
            <CommandPopover
              filterField="groupIds"
              collection={FilterCollection.Groups}
              label="Groups"
              selected={filters.groupIds?.values || []}
            >
              <GroupsCommandList
                multiselect
                onSelect={(ids) =>
                  handleFilterValueChange({
                    field: 'groupIds',
                    values: ids,
                  })
                }
              />
            </CommandPopover>

            <CommandPopover
              filterField="assignedSupervisorId"
              collection={FilterCollection.Users}
              label="Reports To"
              selected={filters.reportToIds?.values || []}
            >
              <UsersCommandList
                multiselect
                onSelect={(ids) =>
                  handleFilterValueChange({
                    field: 'reportToIds',
                    values: ids,
                  })
                }
                contextFilters={{
                  subordinates: {
                    $exists: true,
                  },
                }}
              />
            </CommandPopover>
          </div>
          <div>
            <WithPermission resource="interactions_export" actions={['manage']}>
              <ExportQuery {...query} />
            </WithPermission>
          </div>
        </div>
      </header>
      <Table table={table} verticalLines />
    </div>
  )

  function handleFilterValueChange(opts: {
    field: keyof MetricFilters
    values: string[] | boolean
  }) {
    navigate({
      search: (prev: SearchParams) => {
        function compileFilters() {
          if (typeof opts.values === 'boolean') {
            if (opts.values === true) {
              return {
                ...prev.filters,
                [opts.field]: { op: 'isNotNull' },
              }
            } else {
              return Object.fromEntries(
                Object.entries(prev.filters).filter(([key]) => key !== opts.field),
              )
            }
          }

          if (prev.filters[opts.field]?.op === 'isNotNull') {
            return {
              ...prev.filters,
            }
          }

          return {
            ...prev.filters,
            [opts.field]:
              opts.values.length > 0 ? { op: 'in', values: opts.values } : undefined,
          }
        }

        return {
          ...prev,
          filters: compileFilters(),
        }
      },
    })
  }
}

type LeaderboardRow = {
  handledBy?: Record<string, string>
  scorecardId?: string
  scorecard?: Record<string, string>
  groupId?: string
  group?: Record<string, string>
  aht: string
  totalEvaluations: string
  passRate: string
  aqs: string
  totalInteractions: string
  eligibleInteractions: string
  evaluatedEligibleInteractions: string
  negativeSentimentCount: string
  positiveSentimentCount: string
  neutralSentimentCount: string
  sentimentLift: string
}

const initialData: Array<LeaderboardRow> = []

const columnHelper = createColumnHelper<LeaderboardRow>()

const columns = [
  columnHelper.accessor(
    (row) =>
      'handledBy' in row ? row.handledBy : 'group' in row ? row.group : row.scorecard,
    {
      id: 'name',
      header: 'Name',
      cell: (cell) => {
        const value = cell.getValue()
        if (!value || !('id' in value) || !value.id) {
          return <div className="min-h-10 italic">None</div>
        }

        // Group or Scorecard (both have 'name' property, no firstName/lastName)
        if ('name' in value && !('firstName' in value)) {
          return (
            <div className="flex min-h-10 flex-wrap items-center">
              <span className="mr-2 font-semibold">{value.name}</span>
              {value.workspaceName && (
                <span className="text-accent-foreground text-sm">
                  {value.workspaceName}
                </span>
              )}
            </div>
          )
        }

        // Agent (has firstName/lastName or name fallback)
        if ('firstName' in value) {
          return (
            <Link
              to="/$alias/members/$memberId"
              params={{ memberId: value.id }}
              from="/$alias"
              className="flex min-h-10 items-center gap-3"
            >
              <Avatar {...value} />
              <div>
                <div className="font-semibold">
                  {value.firstName} {value.lastName}
                </div>
              </div>
            </Link>
          )
        }

        // Fallback for any other case
        return (
          <div className="flex min-h-10 flex-wrap items-center">
            <span className="mr-2 font-semibold">{value?.name ?? 'Unknown'}</span>
          </div>
        )
      },
    },
  ),
  columnHelper.accessor('aqs', {
    header: 'Avg QS',
    cell: (cell) => {
      const value = parseFloat(cell.getValue())
      return <div className={cn('text-center font-mono')}>{toPercent(value)}</div>
    },
    size: 100,
    meta: {
      highlight: (cell) => {
        const value = parseFloat(cell.getValue())
        return {
          'bg-green/20': value >= 0.9,
          'bg-red/20': value < 0.7,
        }
      },
      tooltip: 'Average Quality Score',
    },
  }),
  columnHelper.accessor(
    (row) => parseFloat(row.passRate) / parseInt(row.totalEvaluations),
    {
      header: 'Pass Rate',

      cell: (cell) => {
        const value = cell.getValue()
        return <div className={cn('text-center font-mono')}>{toPercent(value)}</div>
      },
      size: 110,
      meta: {
        tooltip: '% of evaluations that met the scorecard goal',
        highlight: (cell) => {
          const value = cell.getValue()
          return {
            'bg-green/20': value >= 0.8,
            'bg-red/20': value < 0.7,
          }
        },
      },
    },
  ),
  columnHelper.accessor('totalInteractions', {
    header: '# Interactions',
    cell: (cell) => {
      const value = cell.getValue()
      return <div className="flex items-center gap-3 font-mono">{value}</div>
    },
    size: 120,
    meta: {
      align: 'right',
      tooltip: 'Total interactions',
    },
  }),
  columnHelper.accessor('aht', {
    header: 'AHT',
    cell: (cell) => {
      const value = cell.getValue()
      return <DurationCell treatZeroAsNull time={value} unit="mm:ss" />
    },
    size: 100,
    meta: {
      tooltip: 'Average handle time',
      align: 'right',
    },
  }),
  columnHelper.accessor(
    (row) => {
      const eligible = Number(row.eligibleInteractions)
      return eligible > 0 ? Number(row.evaluatedEligibleInteractions) / eligible : null
    },
    {
      header: 'Coverage',

      cell: (cell) => {
        const value = cell.getValue()
        return (
          <div className={cn('relative text-center font-mono')}>
            {value === null ? '—' : toPercent(value)}
          </div>
        )
      },
      size: 110,
      meta: {
        tooltip: 'Evaluated / eligible interactions',
        align: 'right',
        highlight: (cell) => {
          const value = cell.getValue()
          return {
            'bg-green/20': value !== null && value >= 0.9,
            'bg-red/20': value !== null && value <= 0.7,
          }
        },
      },
    },
  ),
  columnHelper.accessor(
    (row) => parseFloat(row.positiveSentimentCount) / parseInt(row.totalInteractions),
    {
      header: '% Positive',

      cell: (cell) => {
        const value = cell.getValue()
        return <div className="flex items-center gap-3 font-mono">{toPercent(value)}</div>
      },
      size: 110,
      meta: {
        tooltip: 'Overall positive sentiment',
        align: 'right',
      },
    },
  ),
  columnHelper.accessor(
    (row) => parseFloat(row.neutralSentimentCount) / parseInt(row.totalInteractions),
    {
      header: '% Neutral',

      cell: (cell) => {
        const value = cell.getValue()
        return <div className="flex items-center gap-3 font-mono">{toPercent(value)}</div>
      },
      size: 110,
      meta: {
        tooltip: 'Overall neutral sentiment',
        align: 'right',
      },
    },
  ),
  columnHelper.accessor(
    (row) => parseFloat(row.negativeSentimentCount) / parseInt(row.totalInteractions),
    {
      header: '% Negative',

      cell: (cell) => {
        const value = cell.getValue()
        return <div className="flex items-center gap-3 font-mono">{toPercent(value)}</div>
      },
      size: 120,
      meta: {
        tooltip: 'Overall negative sentiment',
        align: 'right',
      },
    },
  ),
  columnHelper.accessor(
    (row) => parseFloat(row.sentimentLift) / parseInt(row.totalInteractions),
    {
      header: 'Influence',
      cell: (cell) => {
        const value = cell.getValue()
        return (
          <div className={cn('relative text-center font-mono')}>
            <span>{toPercent(value)}</span>
            <span
              className={cn('absolute -inset-4', {
                'bg-green/20': value >= 0.2,
                'bg-red/20': value <= 0,
              })}
            />
          </div>
        )
      },
      size: 110,
      meta: {
        tooltip: 'Sentiment influence, all interactions',
        align: 'right',
      },
    },
  ),
]
